export type ComparisonLevel =
  | "exact"
  | "controlled"
  | "exploratory"
  | "limited"
  | "not_comparable";

export interface ComparableRunMetrics {
  netProfitAmount?: number;
  totalReturnPct?: number;
  maxDrawdownPct?: number;
  winRatePct?: number;
  profitFactor?: number;
  expectancyAmount?: number;
  tradeCount?: number;
  totalFees?: number;
}

export interface ComparableResearchRun {
  id: string;
  symbol?: string;
  timeframe?: string;
  startTime?: string;
  endTime?: string;
  datasetSHA256?: string;
  provider?: string;
  executionContract?: string;
  strategyFingerprint?: string;
  initialCapital?: number;
  tradeDirection?: string;
  // Percentage values use UI-facing percentage points: 2 means 2%, not 0.02.
  commissionRate?: number;
  slippageBps?: number;
  positionSizePct?: number;
  stopLossPct?: number;
  takeProfitPct?: number;
  exitMode?: string;
  metrics: ComparableRunMetrics;
}

export interface ComparisonDifference {
  field: string;
  baseline: string | number | null;
  candidate: string | number | null;
  category: "environment" | "strategy" | "execution";
}

export interface ResearchRunComparison {
  level: ComparisonLevel;
  baselineRunID: string;
  runIDs: string[];
  differences: ComparisonDifference[];
  missingFields: string[];
  warnings: string[];
}

const requiredEnvironmentFields = [
  "symbol",
  "timeframe",
  "startTime",
  "endTime",
  "datasetSHA256",
  "provider",
  "executionContract",
] as const;

const strategyFields = [
  "strategyFingerprint",
  "initialCapital",
  "tradeDirection",
  "commissionRate",
  "slippageBps",
  "positionSizePct",
  "stopLossPct",
  "takeProfitPct",
  "exitMode",
] as const;

type RunField = (typeof requiredEnvironmentFields)[number] | (typeof strategyFields)[number];
const timestampFields: ReadonlySet<RunField> = new Set(["startTime", "endTime"]);

function value(run: ComparableResearchRun, field: RunField): string | number | undefined {
  return run[field];
}

function differenceCategory(field: RunField): ComparisonDifference["category"] {
  if ((requiredEnvironmentFields as readonly string[]).includes(field)) return "environment";
  if ([
    "initialCapital", "commissionRate", "slippageBps", "positionSizePct",
    "stopLossPct", "takeProfitPct",
  ].includes(field)) return "execution";
  return "strategy";
}

function validTimestamp(value: string | number | undefined): number | undefined {
  if (typeof value !== "string" || !value.trim()) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
}

function fieldValuesMatch(
  field: RunField,
  baseline: string | number,
  candidate: string | number,
): boolean | undefined {
  if (!timestampFields.has(field)) return baseline === candidate;
  const baselineTimestamp = validTimestamp(baseline);
  const candidateTimestamp = validTimestamp(candidate);
  if (baselineTimestamp === undefined || candidateTimestamp === undefined) return undefined;
  // Match the native client contract: harmless serializer precision changes
  // up to one second describe the same research interval.
  return Math.abs(baselineTimestamp - candidateTimestamp) <= 1_000;
}

export function compareResearchRuns(
  runs: ComparableResearchRun[],
  baselineRunID: string,
): ResearchRunComparison {
  if (runs.length < 2 || runs.length > 4) {
    throw new Error("comparison_requires_two_to_four_runs");
  }
  const unique = new Map(runs.map((run) => [run.id, run]));
  if (unique.size !== runs.length) throw new Error("comparison_runs_must_be_unique");
  const baseline = unique.get(baselineRunID);
  if (!baseline) throw new Error("comparison_baseline_missing");

  const differences: ComparisonDifference[] = [];
  const missing = new Set<string>();
  for (const run of runs) {
    if (run.id === baseline.id) continue;
    for (const field of [...requiredEnvironmentFields, ...strategyFields]) {
      const baselineValue = value(baseline, field);
      const candidateValue = value(run, field);
      if (baselineValue === undefined || candidateValue === undefined) {
        missing.add(field);
      } else {
        const matches = fieldValuesMatch(field, baselineValue, candidateValue);
        if (matches === undefined) {
          missing.add(field);
          continue;
        }
        if (matches) continue;
        differences.push({
          field,
          baseline: baselineValue,
          candidate: candidateValue,
          category: differenceCategory(field),
        });
      }
    }
  }

  const environmentDifferences = differences.filter((item) => item.category === "environment");
  const intentionalDifferences = differences.filter((item) => item.category !== "environment");
  const explicitIntentionalDifferences = intentionalDifferences.filter(
    (item) => item.field !== "strategyFingerprint",
  );
  const fingerprintChanged = intentionalDifferences.some(
    (item) => item.field === "strategyFingerprint",
  );
  let level: ComparisonLevel;
  if (environmentDifferences.length > 0) {
    level = "not_comparable";
  } else if (missing.size > 0) {
    level = "limited";
  } else if (intentionalDifferences.length === 0) {
    level = "exact";
  } else if (
    new Set(explicitIntentionalDifferences.map((item) => item.field)).size === 1 &&
    explicitIntentionalDifferences.length > 0
  ) {
    // A full strategy fingerprint is expected to change when its one explicit
    // setting changes. Count the explicit setting, not that derived identity,
    // as the controlled variable. A fingerprint-only change stays exploratory
    // because the underlying change is unknown.
    level = "controlled";
  } else if (fingerprintChanged) {
    level = "exploratory";
  } else {
    level = "exploratory";
  }

  const warnings: string[] = [];
  if (level === "not_comparable") warnings.push("comparison_environment_mismatch");
  if (level === "limited") warnings.push("comparison_legacy_fields_missing");
  if (level === "exploratory") warnings.push("comparison_multiple_variables_changed");
  return {
    level,
    baselineRunID,
    runIDs: runs.map((run) => run.id),
    differences,
    missingFields: [...missing].sort(),
    warnings,
  };
}

export function metricDelta(
  baseline: number | undefined,
  candidate: number | undefined,
  lowerIsBetter = false,
): { absolute?: number; favorable?: boolean; neutral?: boolean } {
  if (baseline === undefined || candidate === undefined) return {};
  const absolute = candidate - baseline;
  if (absolute === 0) return { absolute, favorable: false, neutral: true };
  return { absolute, favorable: lowerIsBetter ? absolute < 0 : absolute > 0 };
}
